import React from 'react';
import { X } from 'lucide-react';
import { AgendaCalendar } from './components/AgendaCalendar';
import { ActiveSleepBanner } from '../../shared/components/ActiveSleepBanner';
import { MasterAgendaView } from './components/MasterAgendaView';
import { MasterTimelineView } from './components/MasterTimelineView';
import { TacticalSidebar } from './components/TacticalSidebar';
import { MetabolicHubView } from '../metabolic/MetabolicHubView';
import { MissionBoardHeader } from './components/MissionBoardHeader';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { Event } from '@/types/zod.schemas';
import { useUIStore } from '@stores/v4/useUIStore';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { UI } from '@/shared/design-system/tactical-ui';
import { getDateStyle } from '@/shared/logic/agenda-logic';
import { useMissionBoardState } from './hooks/useMissionBoardState';

interface MasterMissionBoardProps {
    onExit: () => void;
    onNavigate: (view: string | null, state?: any) => void;
    onLogActivity: (date: Date) => void;
    onInitiateClone: (event: Event, date: Date) => void;
    setConfirmDeleteLog: (log: any) => void;
    onStartPlannedSession: (event: Event, retroDate?: string | null) => void;
    onMaximizeRecovery: () => void;
    onMaximizeSession: () => void;
    onToggleSleep: () => void;
    setShowAbandonModal: (show: boolean) => void;
    activeSession: any;
    performanceLabel: string;
    elapsed: string;
    activeRecoverySession: any;
    sleepElapsed: string;
    handleQuickMetabolic: () => void;
    handleQuickNutrition: () => void;
    handleQuickSupplement: (window?: string, scheduledTime?: Date) => void;
    handleReschedule: (eventId: string, typeOrDate: string) => Promise<void>;
    handleManualLog: (event: Event) => Promise<void>;
    handleMarkMissed: (eventId: string) => Promise<void>;
    handleMarkCompleted: (eventId: string) => Promise<void>;
}

/**
 * MasterMissionBoard: The central command center.
 * HARDENED: Ensuring banner clicks switch to session view.
 */
export const MasterMissionBoard: React.FC<MasterMissionBoardProps> = (props) => {
    const {
        selectedDate, setSelectedDate, currentMonth, setCurrentMonth, selectedPlannedEvent,
        isDrawerOpen, isMenuOpen, setIsMenuOpen, projections, events, allEvents,
        activeHistorySession, handleSelectEvent, handleDismissGhost, closeDrawer
    } = useMissionBoardState();

    const { openComposer } = useComposerStore();
    const { agendaViewMode, calendarViewMode, isMetabolicViewActive, setMetabolicViewActive } = useUIStore();

    const handleMaximizeSession = () => {
        props.onMaximizeSession();
        props.onNavigate('session');
    };

    const handleMaximizeRecovery = () => {
        props.onMaximizeRecovery();
        props.onNavigate('session');
    };

    return (
        <div className={UI.layout.container}>
            <div className={UI.layout.scrollable}>
                <MissionBoardHeader 
                    onExit={props.onExit} onToggleSleep={props.onToggleSleep} onLogActivity={props.onLogActivity}
                    onNavigate={props.onNavigate} onQuickMetabolic={props.handleQuickMetabolic}
                    onQuickNutrition={props.handleQuickNutrition} onQuickSupplement={props.handleQuickSupplement}
                    selectedDate={selectedDate} activeRecoverySession={props.activeRecoverySession} projections={projections}
                />

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '20px', padding: '5px 10px 0' }}>
                    <Box>
                        <AgendaCalendar 
                            isGridExpanded={calendarViewMode === "month"} selectedCalendarDate={selectedDate} 
                            setSelectedCalendarDate={setSelectedDate} currentMonth={currentMonth} 
                            setCurrentMonth={setCurrentMonth} getDateStyle={(d) => getDateStyle(d, allEvents || [])} 
                        />
                        {props.activeRecoverySession && <ActiveSleepBanner onClick={handleMaximizeRecovery} elapsed={props.sleepElapsed} />}
                        {props.activeSession && (
                            <Pressable onPress={handleMaximizeSession} style={UI.banner.performance}>
                                <Box style={{ flex: 1 }}>
                                    <Typography style={{ ...UI.text.tinyCaps, color: '#2ecc71', marginBottom: 4 }}>ACTIVE PERFORMANCE</Typography>
                                    <Typography style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>{props.performanceLabel} • {props.elapsed}</Typography>
                                </Box>
                                <Pressable onPress={(e: any) => { e.stopPropagation(); props.setShowAbandonModal(true); }} style={{ padding: 10 }}><X size={20} color="#ef4444" /></Pressable>
                            </Pressable>
                        )}
                    </Box>
                    <div style={{ paddingBottom: '100px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <h3 style={{ ...UI.text.tinyCaps, color: '#555' }}>{selectedDate.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}</h3>
                            {!isMetabolicViewActive && events.length > 0 && <span style={{ ...UI.text.tinyCaps, color: '#f29b11' }}>{events.length} EVENTS</span>}
                        </div>
                        {isMetabolicViewActive ? <MetabolicHubView /> : (agendaViewMode === 'timeline' ? <MasterTimelineView events={events} selectedDate={selectedDate} onSelectEvent={handleSelectEvent} /> : <MasterAgendaView events={events} onSelectEvent={handleSelectEvent} onDismissGhost={handleDismissGhost} selectedDate={selectedDate} />)}
                    </div>
                </div>
            </div>

            <TacticalSidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onNavigate={props.onNavigate} onToggleSleep={props.onToggleSleep} onExit={props.onExit} activeRecoverySession={props.activeRecoverySession} />
        </div>
    );
};
